import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useViewParam } from "@/lib/useViewParam";
import axios from "axios";
import { useAuth } from "@/auth/AuthContext";
import { useRealtimeCtx } from "@/realtime/RealtimeProvider";
import {
  SO_STAGES, stageIndex, stageOf, progressOf, estDeliveryOf,
  soMoney, soDate, soAgo, markThreadRead,
} from "@/store/orders/stages";
import { OrderChatPanel, OrderTimeline, useOrdersChatMeta, SoxIcon } from "@/store/orders/OrderChat";
import { generateAndAttachPackage } from "@/store/orders/projectPdf";
import { QuoteSendOverlay } from "@/studio/screens/preview/SendOverlays";
import { Sk } from "@/components/Skeleton";
import "@/store/orders/orders.css";

/* OrdersWorkspace — the client-detail Orders tab, rebuilt as a visual
 * production-pipeline dashboard (2026-07-13 redesign):
 *   left  — interactive pipeline + premium order cards (progress, timeline,
 *           unread chat, role-aware inline actions — the same transitions the
 *           old SalesOrdersBoard drove; no business logic changed);
 *   right — the order conversation with the Sales team + quick actions.
 * Live-refreshes on the sales-order WebSocket events. */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ASSET = process.env.REACT_APP_BACKEND_URL || "";

/* stage icons for the pipeline nodes */
const STAGE_ICONS = {
  draft_order: SoxIcon.doc, sales_review: SoxIcon.check, awaiting_deposit: SoxIcon.euro,
  in_production: SoxIcon.cube, second_payment: SoxIcon.euro, ready_to_ship: SoxIcon.truck,
};

/* ── pipeline bar ─────────────────────────────────────────────────────── */
function PipelineBar({ counts, filter, onFilter, selectedOrder, loading = false }) {
  const selIdx = selectedOrder ? stageIndex(selectedOrder.status) : -1;
  const declined = selectedOrder && selectedOrder.status === "declined";
  return (
    <div className="sox-panel sox-pipe" data-testid="orders-pipeline">
      <div className="sox-pipe-head">
        <div>
          <div className="sox-pipe-title">Production pipeline</div>
          <div className="sox-pipe-sub">
            {selectedOrder
              ? `Tracking ${selectedOrder.number} — click a stage to filter`
              : "Click a stage to filter orders"}
          </div>
        </div>
        {filter !== "all" && (
          <button className="sox-pipe-clear" onClick={() => onFilter("all")} data-testid="pipeline-clear">
            Clear filter ✕
          </button>
        )}
        <button
          className={`sox-pipe-declined ${filter === "declined" ? "on" : ""}`}
          onClick={() => onFilter(filter === "declined" ? "all" : "declined")}
          data-testid="pipeline-declined"
        >
          {SoxIcon.x(11)} Declined {loading ? <Sk w={12} h={10} r={4} /> : counts.declined || 0}
        </button>
      </div>
      <div className="sox-pipe-track">
        {SO_STAGES.map((s, i) => {
          const done = !declined && selIdx > i;
          const current = !declined && selIdx === i;
          const Icon = STAGE_ICONS[s.key] || SoxIcon.check;
          return (
            <React.Fragment key={s.key}>
              {i > 0 && <div className={`sox-pipe-link ${!declined && selIdx >= i ? "is-done" : ""}`} />}
              <button
                className={`sox-pipe-node ${done ? "is-done" : ""} ${current ? "is-current" : ""} ${filter === s.key ? "is-filter" : ""}`}
                onClick={() => onFilter(filter === s.key ? "all" : s.key)}
                data-testid={`pipeline-${s.key}`} title={`${s.label} — ${counts[s.key] || 0} order(s)`}
              >
                <span className="sox-pipe-dot">
                  {done ? SoxIcon.check(15) : Icon(15)}
                  <span className={`sox-pipe-count ${counts[s.key] && !loading ? "" : "is-zero"}`}>
                    {loading ? <Sk w={10} h={10} r={5} /> : counts[s.key] || 0}
                  </span>
                </span>
                <span className="sox-pipe-label">{s.label}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

/* ── one order card ───────────────────────────────────────────────────── */
function OrderCard({
  o, meta, selected, expanded, clientName, isGlobalSales, busy,
  onSelect, onToggleExpand, actions,
}) {
  const st = stageOf(o.status);
  const pct = progressOf(o);
  const idx = stageIndex(o.status);
  const declined = o.status === "declined";
  const est = estDeliveryOf(o);
  const m = meta || {};
  return (
    <div
      className={`sox-panel sox-order ${selected ? "is-selected" : ""} ${declined ? "is-declined" : ""}`}
      onClick={onSelect} data-testid={`order-card-${o.number}`}
    >
      <div className="sox-order-top">
        <div className="sox-order-tile">{declined ? SoxIcon.x(20) : SoxIcon.cube(20)}</div>
        <div style={{ minWidth: 0 }}>
          <div className="sox-order-id">
            <span className="sox-order-num">{o.number}</span>
            <span className="sox-badge" style={{ background: st.tone + "22", color: st.tone }}>
              <span className="dot" />{st.label}
            </span>
          </div>
          <div className="sox-order-proj"><b>{o.project_name || "Project"}</b>{clientName ? <> · {clientName}</> : null}</div>
        </div>
        <div className="sox-order-val">
          <div className="sox-order-total">{soMoney(o.total)}</div>
          {o.status === "ready_to_ship"
            ? <div className="sox-order-est" style={{ color: "var(--sox-green)" }}>Ready for pickup</div>
            : est ? <div className="sox-order-est">Est. delivery {soDate(est)}</div> : null}
        </div>
      </div>

      {/* progress */}
      {!declined && (
        <div className="sox-prog">
          <div className="sox-prog-bar"><div className={`sox-prog-fill ${pct >= 100 ? "is-full" : ""}`} style={{ width: pct + "%" }} /></div>
          <span className={`sox-prog-pct ${pct >= 100 ? "is-full" : ""}`}>{pct}%</span>
        </div>
      )}

      {/* mini stage track */}
      {!declined && (
        <div className="sox-mini" aria-hidden="true">
          {SO_STAGES.map((s, i) => (
            <div key={s.key} className="sox-mini-step">
              <span className={`sox-mini-dot ${idx > i ? "is-done" : idx === i ? "is-current" : ""}`} title={s.label}>
                {idx > i ? SoxIcon.check(9) : null}
              </span>
              {i < SO_STAGES.length - 1 && <span className={`sox-mini-line ${idx > i ? "is-done" : ""}`} />}
            </div>
          ))}
        </div>
      )}

      {declined && (
        <div className="sox-declined-note" data-testid="declined-note">
          Declined by Sales: {o.decline_reason || "no reason recorded"}
        </div>
      )}

      {/* unread message preview */}
      {m.unread > 0 && m.lastUnread && (
        <div className="sox-lastmsg" data-testid="unread-preview">
          <b>{m.lastUnread.author_name}:</b>
          <span>{m.lastUnread.body}</span>
        </div>
      )}

      {/* meta row */}
      <div className="sox-order-meta">
        <span className="mi">{SoxIcon.cal(12)}<b>{soDate(o.created_at)}</b></span>
        <span className="mi">{SoxIcon.clock(12)}Updated <b>{soAgo(o.updated_at || o.created_at)}</b></span>
        <span className="mi">{SoxIcon.user(12)}<b>{m.salesContact || "LHC Sales Team"}</b></span>
        {(m.attachments || 0) > 0 && <span className="mi">{SoxIcon.clip(12)}<b>{m.attachments}</b></span>}
        <span className={`sox-chatpill ${m.unread ? "has-unread" : ""}`} title={`${m.count || 0} messages`}>
          {SoxIcon.chat(12)} {m.count || 0}
          {m.unread > 0 && <span className="u" data-testid="unread-count">{m.unread}</span>}
        </span>
      </div>

      {/* actions */}
      <div className="sox-order-actions" onClick={(e) => e.stopPropagation()}>
        {actions}
        <button className="sox-abtn a-ghost" style={{ marginLeft: "auto" }} onClick={onToggleExpand} data-testid="order-details-toggle">
          {expanded ? "Hide timeline" : "View timeline"}
        </button>
      </div>

      {expanded && (
        <div className="sox-details" onClick={(e) => e.stopPropagation()}>
          <OrderTimeline order={o} />
        </div>
      )}
    </div>
  );
}

/* ── the workspace ────────────────────────────────────────────────────── */
export default function OrdersWorkspace({ clientId, client, onOpenProject, onViewQuotes }) {
  const { user } = useAuth();
  const rt = useRealtimeCtx();
  const isGlobalSales = user && (user.role === "SUPERADMIN" || user.role === "ADMIN" || (user.role === "SALES" && !user.dealer_id));
  const [orders, setOrders] = useState([]);
  // three-state loading (2026-07-18): the board shows SKELETONS (not "0"
  // pipeline counts / the empty-state panel) until the CURRENT client's
  // orders have actually arrived; the empty state is real data only.
  const [ready, setReady] = useState(false);
  const [filter, setFilter] = useViewParam("filter", "all");
  const [selectedId, setSelectedId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const [declineFor, setDeclineFor] = useState(null);
  const [declineText, setDeclineText] = useState("");
  const [readTick, setReadTick] = useState(0);
  const [sendingOrder, setSendingOrder] = useState(false); // factory→truck moment
  const fileRefs = useRef({});

  // CLIENT ISOLATION (2026-07-13): different client = different orders +
  // different conversations. Switching clients blanks the board immediately
  // (no previous client's orders/chat visible while loading) and a stale
  // fetch for the OLD client can never overwrite the NEW client's rows.
  const clientIdRef = useRef(clientId);
  clientIdRef.current = clientId;
  const load = useCallback(() => {
    const cid = clientId;
    axios.get(`${API}/sales-orders`).then(({ data }) => {
      if (clientIdRef.current !== cid) return;
      setOrders((data || []).filter((o) => !cid || o.client_id === cid));
      setReady(true);
    }).catch(() => { if (clientIdRef.current === cid) { setOrders([]); setReady(true); } });
  }, [clientId]);
  useEffect(() => {
    setOrders([]); setReady(false); setSelectedId(null); setExpandedId(null);
    load();
  }, [load]);
  useEffect(() => { if (rt && rt.lastEvent) load(); }, [rt && rt.lastEvent, load]);

  const { meta, refresh: refreshMeta } = useOrdersChatMeta(
    orders, user && user.id,
    `${(rt && rt.lastEvent && rt.lastEvent.ts) || 0}:${readTick}`);

  const counts = useMemo(() => {
    const c = { declined: 0 };
    for (const o of orders) c[o.status] = (c[o.status] || 0) + 1;
    return c;
  }, [orders]);

  const rows = useMemo(() => {
    const list = filter === "all" ? orders : orders.filter((o) => o.status === filter);
    return list.slice().sort((a, b) => String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || "")));
  }, [orders, filter]);

  const selected = orders.find((o) => o.id === selectedId) || rows[0] || null;

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 2600); };
  const act = async (id, fn, ok) => {
    setBusy(id);
    try { await fn(); flash(ok); load(); }
    catch (e) { flash(e?.response?.data?.detail || "Action failed"); }
    finally { setBusy(""); }
  };
  // Send to Sales = the branded factory→truck→client moment (the animation that
  // used to play on Create Quotation — moved here 2026-07-13) while the PDF
  // documentation package generates + attaches and the order submits (≥2.4s).
  // Package generation stays best-effort — a PDF failure never blocks the send.
  const sendToSales = (o) => act(o.id, async () => {
    setSendingOrder(true);
    const minShow = new Promise((r) => setTimeout(r, 2400));
    try {
      let pdfOk = true;
      try { await generateAndAttachPackage({ order: o, client }); }
      catch (e) { pdfOk = false; }
      await axios.post(`${API}/sales-orders/${o.id}/send-to-sales`);
      await minShow;
      if (!pdfOk) throw Object.assign(new Error("sent-no-pdf"), {
        response: { data: { detail: "Sent to Sales — the PDF package could not be generated." } } });
    } finally {
      await minShow.catch(() => {});
      setSendingOrder(false);
    }
  }, "Sent to Sales — documentation package attached");
  const approve = (o) => act(o.id, () => axios.post(`${API}/sales-orders/${o.id}/decision`, { decision: "approve" }), "Approved");
  const submitDecline = () => {
    const o = declineFor; if (!o) return;
    act(o.id, () => axios.post(`${API}/sales-orders/${o.id}/decision`, { decision: "decline", comment: declineText }), "Declined")
      .then(() => { setDeclineFor(null); setDeclineText(""); });
  };
  const verify = (o, stage) => act(o.id, () => axios.post(`${API}/sales-orders/${o.id}/verify-payment`, { stage, approve: true }), "Payment verified");
  const advance = (o, to) => act(o.id, () => axios.post(`${API}/sales-orders/${o.id}/advance`, { to }), "Advanced");
  const upload = (o, stage, file) => act(o.id, async () => {
    const fd = new FormData(); fd.append("file", file);
    await axios.post(`${API}/sales-orders/${o.id}/upload-proof?stage=${stage}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  }, "Proof uploaded");

  const proofBlock = (o, stage) => {
    const proofs = (stage === "awaiting_deposit" ? o.deposit_proofs : o.second_payment_proofs) || [];
    const canUpload = !isGlobalSales;
    return (
      <div style={{ width: "100%" }}>
        {proofs.length > 0 && (
          <div className="sox-proofs">
            {proofs.map((p, i) => (
              <a key={i} className="sox-proof" href={ASSET + p.url} target="_blank" rel="noreferrer">{SoxIcon.clip(12)}{p.filename || "proof"}</a>
            ))}
          </div>
        )}
        <div className="sox-order-actions" style={{ marginTop: proofs.length ? 10 : 0 }}>
          {canUpload && (
            <>
              <input type="file" style={{ display: "none" }} ref={(el) => { fileRefs.current[o.id] = el; }}
                onChange={(e) => { const f = e.target.files[0]; if (f) upload(o, stage, f); e.target.value = ""; }} />
              <button className="sox-abtn a-sky" disabled={busy === o.id} onClick={() => fileRefs.current[o.id] && fileRefs.current[o.id].click()}>
                {SoxIcon.upload(13)}{proofs.length ? "Upload another proof" : "Upload payment proof"}
              </button>
            </>
          )}
          {!canUpload && proofs.length === 0 && <span className="sox-wait"><span className="spin" />Waiting for the store's payment proof…</span>}
          {isGlobalSales && proofs.length > 0 && (
            <button className="sox-abtn a-green" disabled={busy === o.id} onClick={() => verify(o, stage)}>{SoxIcon.check(13)}Verify payment</button>
          )}
        </div>
      </div>
    );
  };

  const actionsFor = (o) => {
    if (o.status === "draft_order" && !isGlobalSales)
      return <button className="sox-abtn a-primary" disabled={busy === o.id} onClick={() => sendToSales(o)} data-testid="send-to-sales">Send to Sales Team →</button>;
    if (o.status === "sales_review" && isGlobalSales) return (
      <>
        <button className="sox-abtn a-green" disabled={busy === o.id} onClick={() => approve(o)}>{SoxIcon.check(13)}Approve</button>
        <button className="sox-abtn a-red" disabled={busy === o.id} onClick={() => { setDeclineFor(o); setDeclineText(""); }}>Decline</button>
      </>
    );
    if (o.status === "sales_review" && !isGlobalSales)
      return <span className="sox-wait"><span className="spin" />Awaiting Sales review…</span>;
    if (o.status === "awaiting_deposit") return proofBlock(o, "awaiting_deposit");
    if (o.status === "in_production") return isGlobalSales
      ? <button className="sox-abtn a-pink" disabled={busy === o.id} onClick={() => advance(o, "second_payment")}>Request second payment →</button>
      : <span className="sox-wait"><span className="spin" />In production — we'll notify you here</span>;
    if (o.status === "second_payment") return proofBlock(o, "second_payment");
    if (o.status === "ready_to_ship")
      return <span style={{ fontSize: 12.5, color: "var(--sox-green)", fontWeight: 800, display: "inline-flex", alignItems: "center", gap: 6 }}>{SoxIcon.truck(14)}Ready to ship ✓</span>;
    return null;
  };

  /* chat context — stays visible while chatting (spec: project chat context) */
  const contextRows = selected ? [
    { k: "Client", v: (client && client.name) || "—" },
    { k: "Project", v: selected.project_name || "—" },
    { k: "Order", v: selected.number },
    { k: "Status", v: stageOf(selected.status).label },
    { k: "Sales contact", v: (meta[selected.id] && meta[selected.id].salesContact) || "LHC Sales Team" },
    { k: "Value", v: soMoney(selected.total) },
  ] : [];

  const markAllRead = () => {
    for (const o of orders) if ((meta[o.id] || {}).unread) markThreadRead(o.id);
    setReadTick((t) => t + 1);
    refreshMeta();
  };

  return (
    <div className="sox" data-testid="orders-workspace">
      <div className="sox-layout">
        {/* ── left: pipeline + cards ── */}
        <div className="sox-main">
          {msg && <div className="sox-toast" data-testid="orders-toast">{msg}</div>}
          <PipelineBar counts={counts} filter={filter} onFilter={setFilter} selectedOrder={selected} loading={!ready} />

          {!ready ? (
            <div className="sox-cards" data-loading="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="sox-panel" style={{ overflow: "hidden", padding: 0 }}>
                  <Sk w="100%" h={104} r={0} style={{ display: "block" }} />
                </div>
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="sox-panel sox-empty lhc-reveal" data-testid="orders-empty">
              <div className="big">{SoxIcon.cube(24)}</div>
              <b>{filter === "all" ? "No orders yet" : `Nothing in “${filter === "declined" ? "Declined" : stageOf(filter).label}”`}</b>
              {filter === "all"
                ? "Create an order from an approved quote — it will move through the pipeline here."
                : "Orders will appear here as they reach this stage."}
            </div>
          ) : (
            <div className="sox-cards lhc-reveal">
              {rows.map((o) => (
                <OrderCard
                  key={o.id} o={o} meta={meta[o.id]} clientName={client && client.name}
                  selected={selected && selected.id === o.id}
                  expanded={expandedId === o.id}
                  isGlobalSales={isGlobalSales} busy={busy}
                  onSelect={() => setSelectedId(o.id)}
                  onToggleExpand={() => setExpandedId((id) => (id === o.id ? null : o.id))}
                  actions={actionsFor(o)}
                />
              ))}
            </div>
          )}
        </div>

        {/* ── right: conversation + quick actions ── */}
        <aside className="sox-side">
          <OrderChatPanel
            order={selected} me={user}
            counterpartName={isGlobalSales ? ((client && client.name) || "Store") : "Sales Team"}
            contextRows={contextRows}
            online={!!(rt && rt.connected)}
            lastEvent={rt && rt.lastEvent}
            onThreadChange={() => { setReadTick((t) => t + 1); }}
          />
          <div className="sox-panel sox-qa">
            <div className="sox-qa-title">Quick actions</div>
            <div className="sox-qa-row">
              <button className="sox-qa-btn" disabled={!selected} data-testid="qa-open-project"
                onClick={() => selected && onOpenProject && onOpenProject(selected.project_id)}>
                {SoxIcon.folder(16)}Open project
              </button>
              <button className="sox-qa-btn" data-testid="qa-view-quotes" onClick={() => onViewQuotes && onViewQuotes()}>
                {SoxIcon.doc(16)}View quotes
              </button>
              <button className="sox-qa-btn" data-testid="qa-mark-read" onClick={markAllRead}>
                {SoxIcon.check(16)}Mark all read
              </button>
            </div>
          </div>
        </aside>
      </div>

      {/* Branded send moment — factory → truck → Sales (ex-Create-Quotation scene) */}
      {sendingOrder && <QuoteSendOverlay caption="Sending order to the Sales team…" />}

      {/* decline modal */}
      {declineFor && (
        <div className="sox-modal-bg" onClick={() => setDeclineFor(null)}>
          <div className="sox-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Decline order {declineFor.number}</h3>
            <p>A reason is required — the store will see it.</p>
            <textarea autoFocus rows={4} value={declineText} onChange={(e) => setDeclineText(e.target.value)} placeholder="Reason for declining…" />
            <div className="sox-modal-btns">
              <button className="sox-abtn a-ghost" onClick={() => setDeclineFor(null)}>Cancel</button>
              <button className="sox-abtn a-red" disabled={!declineText.trim()} onClick={submitDecline}>Decline order</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
